import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import MacroItem from './MacroItem';
import AddButton from './AddButton';
import LanguageSelector from './LanguageSelector';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 15px;
  margin-bottom: 30px;
  padding: 10px;
  position: relative;
  z-index: 100;
  /* Ensure grid items stay within bounds */
  overflow: visible;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  /* Prevent grid from exceeding container width */
  min-width: 0;
  /* Ensure at least 2 columns on very narrow screens */
  @supports (grid-template-columns: repeat(auto-fit, minmax(120px, 1fr))) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  }
  
  /* Add subtle background for better glassmorphism effect */
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    background: linear-gradient(135deg, 
      rgba(255, 255, 255, 0.02) 0%, 
      rgba(255, 255, 255, 0.01) 50%, 
      rgba(255, 255, 255, 0.02) 100%);
    border-radius: 20px;
    z-index: -1;
    opacity: 0.5;
  }
  
  /* Responsive adjustments - fallback for browsers that don't support auto-fit */
  @media (max-width: 400px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    padding: 6px;
  }
  
  @media (max-width: 280px) {
    grid-template-columns: 1fr;
    gap: 6px;
    padding: 4px;
  }
`;

/**
 * Component for rendering a grid of macros
 */
const MacroList = ({
  predefinedMacros,
  customMacros = [],
  languages = [],
  showLanguageDropdown = false,
  selectedLanguage = 'en',
  onLanguageSelect,
  onLanguageDropdownClose,
  onAddMacroClick,
  onCustomMacroDelete,
  onCustomMacroEdit,
  onPredefinedMacroEdit,
  onPredefinedMacroDelete
}) => {
  return (
    <Grid>
      {/* Translate Conversation macro */}
      <MacroItem
        icon={predefinedMacros.translate.icon}
        name={predefinedMacros.translate.name}
        onClick={predefinedMacros.translate.onClick}
        showEdit={true}
        isPredefined={true}
        onEdit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          predefinedMacros.translate.onEdit && predefinedMacros.translate.onEdit(e);
        }}
      >
        {showLanguageDropdown && (
          <LanguageSelector 
            languages={languages} 
            onSelect={onLanguageSelect} 
            onMouseLeave={onLanguageDropdownClose}
          />
        )}
      </MacroItem>

      {/* Find Income macro - only show if not deleted */}
      {predefinedMacros.income && (
        <MacroItem
          icon={predefinedMacros.income.icon}
          customIcon={predefinedMacros.income.customIcon}
          name={predefinedMacros.income.name}
          onClick={predefinedMacros.income.onClick}
          onContextMenu={predefinedMacros.income.onContextMenu}
          showDelete={true}
          showEdit={true}
          isPredefined={true}
          onDelete={(e) => onPredefinedMacroDelete('income', e)}
          onEdit={(e) => onPredefinedMacroEdit('income', predefinedMacros.income, e)}
        />
      )}

      {/* Calculate Balance macro - only show if not deleted */}
      {predefinedMacros.balance && (
        <MacroItem
          icon={predefinedMacros.balance.icon}
          customIcon={predefinedMacros.balance.customIcon}
          name={predefinedMacros.balance.name}
          onClick={predefinedMacros.balance.onClick}
          onContextMenu={predefinedMacros.balance.onContextMenu}
          showDelete={true}
          showEdit={true}
          isPredefined={true}
          onDelete={(e) => onPredefinedMacroDelete('balance', e)}
          onEdit={(e) => onPredefinedMacroEdit('balance', predefinedMacros.balance, e)}
        />
      )}
      
      {/* Custom macros */}
      {customMacros && customMacros.map(macro => (
        <MacroItem
          key={macro.id}
          icon={macro.icon}
          customIcon={macro.customIcon}
          name={macro.name}
          onClick={() => predefinedMacros.custom.onClick(macro)}
          showDelete={true}
          showEdit={true}
          onDelete={(e) => onCustomMacroDelete(macro.id, e)}
          onEdit={(e) => onCustomMacroEdit(macro, e)}
        />
      ))}
      
      {/* Add Custom Macro button */}
      <AddButton onClick={onAddMacroClick} />
    </Grid>
  );
};

MacroList.propTypes = {
  predefinedMacros: PropTypes.shape({
    translate: PropTypes.shape({
      icon: PropTypes.node.isRequired,
      name: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      onContextMenu: PropTypes.func,
      onEdit: PropTypes.func
    }).isRequired,
    income: PropTypes.shape({
      icon: PropTypes.node.isRequired,
      name: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      onContextMenu: PropTypes.func
    }).isRequired,
    balance: PropTypes.shape({
      icon: PropTypes.node.isRequired,
      name: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      onContextMenu: PropTypes.func
    }).isRequired,
    custom: PropTypes.shape({
      onClick: PropTypes.func.isRequired
    }).isRequired
  }).isRequired,
  customMacros: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      icon: PropTypes.node.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      region: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  showLanguageDropdown: PropTypes.bool,
  selectedLanguage: PropTypes.string,
  onLanguageSelect: PropTypes.func.isRequired,
  onLanguageDropdownClose: PropTypes.func.isRequired,
  onAddMacroClick: PropTypes.func.isRequired,
  onCustomMacroDelete: PropTypes.func.isRequired,
  onCustomMacroEdit: PropTypes.func,
  onPredefinedMacroEdit: PropTypes.func,
  onPredefinedMacroDelete: PropTypes.func
};

export default MacroList; 